import { useEffect, useState } from 'react';
import { CodigoSeis } from '../componentes/CodigoSeis';
import { Icono } from '../componentes/Icono';
import { minutosYSegundos, textoErrorCodigo, type ErrorCodigo } from './logica';
import { AvisoPc, BotonPc, LegalCaptcha } from './Pagina';
import { D, T } from './textos';

/**
 * Paso «Escribe el código» (T7) del bloque de identificarse (2.9) y de los cambios de contacto de «Mis datos» (2.10): 6
 * números (`CodigoSeis`), «Caduca en m:ss» con `expires_at`, «Reenviar en N s» con `resend_after_seconds` y, pasada esa
 * espera con teléfono, «¿No te llega? Recíbelo por SMS o email» (T8, G154). Errores de G153: incorrecto con los intentos
 * que quedan; caducado o agotado cambian el botón a «Pedir otro código».
 */
export interface PropsPasoCodigo {
    destino: string;
    cambiar?: { texto: string; accion: () => void };
    expira: string;
    /** Momento (ms) desde el que se puede reenviar. */
    reenviarDesde: number;
    codigo: string;
    onCodigo: (codigo: string) => void;
    error: ErrorCodigo | null;
    otroEnviado: boolean;
    ocupado: boolean;
    /** Con teléfono por WhatsApp: otros canales tras la espera (G154). */
    noLlega?: { sms: () => void; email?: () => void };
    onComprobar: (codigo: string) => void;
    onReenviar: () => void;
    ahora?: () => number;
}

export function PasoCodigo({ destino, cambiar, expira, reenviarDesde, codigo, onCodigo, error, otroEnviado, ocupado, noLlega, onComprobar, onReenviar, ahora = Date.now }: PropsPasoCodigo) {
    const [momento, setMomento] = useState(ahora);
    useEffect(() => {
        const t = setInterval(() => {
            setMomento(ahora());
        }, 1000);
        return () => {
            clearInterval(t);
        };
    }, [ahora]);

    const quedan = Math.ceil((Date.parse(expira) - momento) / 1000);
    const caducado = error?.tipo === 'caducado' || quedan <= 0;
    const agotado = error?.tipo === 'agotado';
    const fuera = caducado || agotado;
    const espera = Math.max(0, Math.ceil((reenviarDesde - momento) / 1000));
    const errorVisible: ErrorCodigo | null = agotado ? { tipo: 'agotado' } : caducado ? { tipo: 'caducado' } : error;

    return (
        <section className="rv-pc-card" aria-labelledby="rv-pc-codigo-t">
            <div className="rv-pc-card__cab">
                <h2 id="rv-pc-codigo-t">{T.t7.titulo}</h2>
                <span>{destino}</span>
                {cambiar !== undefined && (
                    <button type="button" className="rv-pc-l" style={{ margin: '-8px 0 -10px -6px' }} onClick={cambiar.accion}>
                        <Icono nombre="pencil" />
                        <span>{cambiar.texto}</span>
                    </button>
                )}
            </div>
            <div className="rv-pc-campos">
                <div className={fuera ? 'rv-pc-codigo--apagado' : undefined}>
                    <CodigoSeis
                        valor={codigo}
                        onChange={onCodigo}
                        error={error?.tipo === 'incorrecto'}
                        autoFocus
                        onCompleto={(c) => {
                            if (!ocupado && !fuera) {
                                onComprobar(c);
                            }
                        }}
                    />
                </div>
                <div className="rv-pc-tiempo">
                    <span role="timer" className={fuera ? 'rv-pc-tiempo--fuera' : undefined}>
                        <Icono nombre="clock" />
                        <span>{agotado ? D.sinIntentos : caducado ? D.caducado : D.caducaEn(minutosYSegundos(quedan))}</span>
                    </span>
                    <button type="button" className="rv-pc-l" style={{ marginRight: -6 }} disabled={espera > 0 || ocupado || fuera} onClick={onReenviar}>
                        <Icono nombre="rotate-ccw" />
                        <span>{espera > 0 ? D.reenviarEn(espera) : D.reenviar}</span>
                    </button>
                </div>
                {noLlega !== undefined && espera === 0 && !fuera && (
                    <div className="rv-pc-caja">
                        <strong>{T.t8}</strong>
                        <div className="rv-pc-caja__botones">
                            <BotonPc tipo="sec" peq icono="message-square-text" texto={D.porSms} onClick={noLlega.sms} />
                            {noLlega.email !== undefined && <BotonPc tipo="sec" peq icono="mail" texto={D.porEmail} onClick={noLlega.email} />}
                        </div>
                    </div>
                )}
            </div>
            {errorVisible !== null ? (
                <AvisoPc texto={textoErrorCodigo(errorVisible)} tipo={errorVisible.tipo === 'caducado' ? 'espera' : 'error'} />
            ) : (
                otroEnviado && <AvisoPc texto={D.otroEnviado} tipo="info" />
            )}
            <div className="rv-pc-acciones">
                {fuera ? (
                    <BotonPc texto={D.pedirOtro} icono="rotate-ccw" ocupado={ocupado} ocupadoTexto={D.enviando} onClick={onReenviar} />
                ) : (
                    <BotonPc
                        texto={D.comprobar}
                        icono="check"
                        ocupado={ocupado}
                        ocupadoTexto={D.comprobando}
                        onClick={() => {
                            onComprobar(codigo);
                        }}
                    />
                )}
                {fuera && <LegalCaptcha />}
            </div>
        </section>
    );
}
